import React from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Zoom } from "@mui/material";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { Autocomplete } from "@mui/material";
import AnswerDetailsEditor from "./AnswerDetailsEditor";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Zoom style={{ transitionDelay: "150ms" }} ref={ref} {...props} />;
});

const emptyRow = {
  code: "",
  name: "",
  description: "",
  questionnaire_id: "",
  answer_type: "radio",
  answer_details: [],
};

const answerTypeOptions = [
  { value: "radio", label: "Radio" },
  { value: "checkbox", label: "Checkbox" },
  { value: "text", label: "Text" },
];

export default function AnswersDialog({ open, row, questions = [], defaultQuestionId = "", onClose, onSubmit }) {
  const [codeError, setCodeError] = React.useState(false);
  const [form, setForm] = React.useState(
    row
      ? { ...emptyRow, ...row, questionnaire_id: row?.questionnaire?.id || row?.questionnaire_id || defaultQuestionId }
      : { ...emptyRow, questionnaire_id: defaultQuestionId }
  );

  const [answerDetails, setAnswerDetails] = React.useState(() => row?.answer_details || []);

  React.useEffect(() => {
    if (row) {
      setForm({
        ...emptyRow,
        ...row,
        questionnaire_id: row?.questionnaire?.id || row?.questionnaire_id || defaultQuestionId,
        answer_type: row?.answer_type || "radio",
      });
      setAnswerDetails(row?.answer_details || []);
    } else {
      setForm({ ...emptyRow, questionnaire_id: defaultQuestionId });
      setAnswerDetails([]);
    }
    setCodeError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row, defaultQuestionId]);

  const handleChange = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
  };

  const handleSave = () => {
    const code = String(form?.code || "").trim();
    if (!code) {
      setCodeError(true);
      return;
    }

    onSubmit?.({
      id: row?.id,
      code,
      name: form?.name,
      description: form?.description,
      questionnaire_id: form?.questionnaire_id,
      answer_type: form?.answer_type,
      answer_details: Array.isArray(answerDetails) ? answerDetails : [],
    });
  };

  return (
    <Dialog open={open} TransitionComponent={Transition} keepMounted fullWidth maxWidth={"sm"}>
      <DialogTitle color={"#616B88"} sx={{ fontSize: 24 }} className={"text-bold"} fontWeight={700}>
        {row ? "Edit Answer Set" : "Add Answer Set"}
      </DialogTitle>
      <Box className={"common-divider"} />
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              id="question-id"
              options={questions}
              value={questions.find((q) => String(q.id) === String(form.questionnaire_id)) || null}
              size={"small"}
              onChange={(e, next) => handleChange("questionnaire_id", next?.id || "")}
              getOptionLabel={(o) => o?.question || o?.name || o?.code || o?.id || ""}
              renderInput={(params) => <TextField {...params} label="Question" />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={answerTypeOptions}
              value={answerTypeOptions.find((o) => o.value === form.answer_type) || null}
              size={"small"}
              onChange={(_, next) => handleChange("answer_type", next?.value || "radio")}
              getOptionLabel={(o) => o?.label || ""}
              renderInput={(params) => <TextField {...params} label="Answer Type" />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size={"small"}
              label="Code"
              value={form.code}
              error={codeError && !String(form.code || "").trim()}
              helperText={codeError && !String(form.code || "").trim() ? "Required" : ""}
              onChange={(e) => {
                setCodeError(false);
                handleChange("code", e.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size={"small"}
              label="Name"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size={"small"}
              label="Description"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <AnswerDetailsEditor value={answerDetails} onChange={setAnswerDetails} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant={"outlined"} color={"error"} className={"text-capitalize"}>
          cancel
        </Button>
        <Button onClick={handleSave} variant={"contained"} className={"text-capitalize"}>
          {row ? "Update" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
